import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    service: '',
    projectDetails: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Save to database
      const { error } = await supabase
        .from('contact_enquiries')
        .insert([
          {
            full_name: formData.fullName,
            company_name: formData.companyName,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            project_details: formData.projectDetails,
          }
        ]);

      if (error) {
        console.error('Supabase error:', error);
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Send email notification using FormSubmit
      const emailFormData = new FormData();
      emailFormData.append('_subject', `New Enquiry from ${formData.fullName}`);
      emailFormData.append('Full Name', formData.fullName);
      emailFormData.append('Company', formData.companyName || 'Not provided');
      emailFormData.append('Email', formData.email);
      emailFormData.append('Phone', formData.phone);
      emailFormData.append('Service', formData.service || 'Not specified');
      emailFormData.append('Project Details', formData.projectDetails || 'Not provided');
      emailFormData.append('_template', 'table');
      emailFormData.append('_captcha', 'false');

      await fetch('https://formsubmit.co/greylingfanie82@gmail.com', {
        method: 'POST',
        body: emailFormData,
      });

      setSubmitStatus('success');
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        service: '',
        projectDetails: '',
      });
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <h2 className="text-3xl font-bold text-navy mb-8">Send Us an Enquiry</h2>
            
            {submitStatus === 'success' && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  <span className="font-semibold">Thank you for your enquiry!</span>
                </div>
                <p className="mt-1 text-sm">We'll be in touch with you shortly.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-fill text-xl"></i>
                  <span className="font-semibold">Something went wrong</span>
                </div>
                <p className="mt-1 text-sm">Please try again or contact us directly.</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-charcoal font-semibold mb-2">
                  Full Name <span className="text-gold">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors"
                />
              </div>

              <div>
                <label htmlFor="companyName" className="block text-charcoal font-semibold mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-charcoal font-semibold mb-2">
                  Email Address <span className="text-gold">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-charcoal font-semibold mb-2">
                  Phone Number <span className="text-gold">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-charcoal font-semibold mb-2">
                  Service Required
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Commercial Fit-Outs">Commercial Fit-Outs</option>
                  <option value="Switchboard Installations & Upgrades">Switchboard Installations & Upgrades</option>
                  <option value="Compliance Testing & Certification">Compliance Testing & Certification</option>
                  <option value="Fault Finding & Diagnostics">Fault Finding & Diagnostics</option>
                  <option value="Lighting & Power Installations">Lighting & Power Installations</option>
                  <option value="Preventative Maintenance">Preventative Maintenance</option>
                </select>
              </div>

              <div>
                <label htmlFor="projectDetails" className="block text-charcoal font-semibold mb-2">
                  Project Details
                </label>
                <textarea
                  id="projectDetails"
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={handleChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors resize-none"
                  placeholder="Please provide details about your project..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gold hover:bg-amber-500 text-navy px-8 py-4 rounded-lg font-bold text-base tracking-wider whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line text-xl animate-spin"></i>
                    SENDING...
                  </>
                ) : (
                  <>
                    SUBMIT ENQUIRY
                    <i className="ri-arrow-right-line text-xl"></i>
                  </>
                )}
              </button>

              <p className="text-sm text-gray-500 text-center">
                We typically respond within 24 hours
              </p>
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-navy rounded-lg p-8 text-white sticky top-24">
              <h3 className="text-gold font-bold text-lg tracking-wider mb-6">CONTACT DETAILS</h3>
              
              <div className="space-y-6">
                <a href="tel:+61400000000" className="flex items-start gap-4 hover:text-gold transition-colors cursor-pointer group">
                  <i className="ri-phone-fill text-gold text-2xl mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Phone</p>
                    <p className="font-bold text-xl group-hover:text-gold">0400 000 000</p>
                  </div>
                </a>

                <div className="h-px bg-gold/20"></div>

                <a href="mailto:info@nselectricalwa.com.au" className="flex items-start gap-4 hover:text-gold transition-colors cursor-pointer group">
                  <i className="ri-mail-fill text-gold text-2xl mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Email</p>
                    <p className="font-semibold group-hover:text-gold">info@nselectricalwa.com.au</p>
                  </div>
                </a>

                <div className="h-px bg-gold/20"></div>

                <div className="flex items-start gap-4">
                  <i className="ri-shield-check-fill text-gold text-2xl mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">EC Licence</p>
                    <p className="font-bold text-lg">12345</p>
                  </div>
                </div>

                <div className="h-px bg-gold/20"></div>

                <div className="flex items-start gap-4">
                  <i className="ri-checkbox-circle-fill text-gold text-2xl mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Insurance</p>
                    <p className="font-semibold">Fully Insured</p>
                  </div>
                </div>

                <div className="h-px bg-gold/20"></div>

                <div className="flex items-start gap-4">
                  <i className="ri-map-pin-fill text-gold text-2xl mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Service Area</p>
                    <p className="font-semibold">Western Australia</p>
                  </div>
                </div>
              </div>

              <a
                href="tel:+61400000000"
                className="mt-8 w-full bg-white text-navy px-6 py-3 rounded-lg font-bold text-sm text-center hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-fill text-lg"></i>
                CALL NOW
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;